import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import Chart from 'chart.js/auto';
import { rateAlarmEntries } from '../../../utils/feedingRates';
import { Grid, Popper, Box, Typography } from '@mui/material';

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function parseDateSafe(dateValue) {
  if (!dateValue || typeof dateValue !== 'string') return null;

  if (dateValue.includes('/')) {
    const [day, month, year] = dateValue.split('/');
    return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  }

  if (dateValue.includes('-')) {
    const parts = dateValue.split('-');

    if (parts[0]?.length === 4) {
      const [year, month, day] = parts;
      return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }

    const [day, month, year] = parts;
    return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  }

  return null;
}

function getISOWeek(date) {
  const temp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  temp.setUTCDate(temp.getUTCDate() + 4 - (temp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(temp.getUTCFullYear(), 0, 1));
  return Math.ceil(((temp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function formatMonthLabel(date) {
  return `${MONTHS_ES[date.getMonth()]} ${date.getFullYear()}`;
}

function matchesPeriodLabel(dayValue, selectedLabel) {
  if (!selectedLabel) return true;
  if (dayValue === selectedLabel) return true;

  const parsedDate = parseDateSafe(dayValue);
  if (!parsedDate) return false;

  if (/^Semana\s+\d+/i.test(selectedLabel)) {
    return `Semana ${getISOWeek(parsedDate)}` === selectedLabel;
  }

  if (/^[A-Za-zÁÉÍÓÚáéíóúñÑ]+\s+\d{4}$/.test(selectedLabel)) {
    return formatMonthLabel(parsedDate) === selectedLabel;
  }

  return false;
}

function formatDisplayDay(dayValue) {
  const parsedDate = parseDateSafe(dayValue);
  if (!parsedDate) return dayValue;

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const year = parsedDate.getFullYear();
  return `${day}/${month}/${year}`;
}

function TooltipBody({ color, currentRateKey, value, rateEntries, translate }) {
  const t = (key, fallback) => {
    const text = typeof translate === 'function' ? translate(key) : key;
    return text && text !== key ? text : fallback;
  };

  const rateLabelMap = {
    baja: t('low', 'Baja'),
    media: t('medium', 'Media'),
    alta: t('high', 'Alta'),
  };

  return (
    <div style={{ maxWidth: '35vw' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color, mb: '.5vh', fontSize: '.85vw' }}>
        {t('rate', 'Tasa')} {rateLabelMap[currentRateKey] || currentRateKey}
      </Typography>
      <Typography variant="body2" sx={{ mb: '.5vh', color: '#c3c3c3', fontSize: '.7vw' }}>
        {t('alarms', 'Alarmas')}: <span style={{ color }}>{value}</span>
      </Typography>
      <Typography variant="body2" sx={{ mb: '.5vh', color: '#c3c3c3', fontSize: '.7vw' }}>
        {t('rates', 'Tasas')}:
      </Typography>
      <ul style={{ paddingLeft: '1vw', margin: 0, maxHeight: '40vh', overflowY: 'auto' }}>
        {rateEntries.map((entry, idx) => {
          const formattedValue =
            typeof entry.value === 'number' && entry.value % 1 !== 0
              ? entry.value.toFixed(2)
              : entry.value;

          return (
            <li key={`${entry.rawDay}-${entry.value}-${idx}`} style={{ color: '#c3c3c3', fontSize: '.65vw' }}>
              {formattedValue} {entry.unit}
              <span style={{ color: '#c3c3c3', fontSize: '.55vw', opacity: '0.7' }}>
                {` - ${entry.day}${entry.names ? ` - ${entry.names}` : ''}`}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const DoughnutChart = ({ chartData = [], jaulaSeleccionada, selectedDay, dias, chartInstanceRef, translate, selectedRateKey = 'auto' }) => {
  const t = (key, fallback) => {
    const value = typeof translate === 'function' ? translate(key) : key;
    return value && value !== key ? value : fallback;
  };

  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const hideTimerRef = useRef(null);
  const hideScheduledRef = useRef(false);
  const tooltipLockedRef = useRef(false);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipData, setTooltipData] = useState({
    placement: 'bottom',
    color: '#fff',
    currentRateKey: 'baja',
    value: '',
    rateEntries: [],
  });

  const computeAlarmsByTasaAcumulado = useCallback((jaula, day = null) => {
    const tasaData = rateAlarmEntries(jaula, selectedRateKey);
    let sumBaja = 0;
    let sumMedia = 0;
    let sumAlta = 0;

    tasaData.forEach((item) => {
      if (!matchesPeriodLabel(item.day, day)) return;
      sumBaja += item?.value?.baja?.value || 0;
      sumMedia += item?.value?.media?.value || 0;
      sumAlta += item?.value?.alta?.value || 0;
    });

    return { baja: sumBaja, media: sumMedia, alta: sumAlta };
  }, [selectedRateKey]);

  const jaulaFiltrada = useMemo(
    () => chartData.find((jaula) => jaula.name === jaulaSeleccionada),
    [chartData, jaulaSeleccionada]
  );

  const alarmsByTasaAcumulado = useMemo(() => {
    return jaulaFiltrada
      ? computeAlarmsByTasaAcumulado(jaulaFiltrada, selectedDay)
      : chartData.reduce(
          (acc, jaula) => {
            const current = computeAlarmsByTasaAcumulado(jaula, selectedDay);
            return {
              baja: acc.baja + current.baja,
              media: acc.media + current.media,
              alta: acc.alta + current.alta,
            };
          },
          { baja: 0, media: 0, alta: 0 }
        );
  }, [chartData, computeAlarmsByTasaAcumulado, jaulaFiltrada, selectedDay]);

  const sumDonut =
    alarmsByTasaAcumulado.baja + alarmsByTasaAcumulado.media + alarmsByTasaAcumulado.alta;

  const doughnutData = useMemo(() => {
    return sumDonut === 0
      ? {
          labels: [
            typeof translate === 'function' && translate('no_data') !== 'no_data'
              ? translate('no_data')
              : 'Sin datos',
          ],
          datasets: [
            {
              data: [1],
              backgroundColor: ['rgba(255, 255, 255, 0.12)'],
              borderColor: ['rgba(255, 255, 255, 0.12)'],
              borderWidth: 0.2,
            },
          ],
        }
      : {
          labels: [
            typeof translate === 'function' && translate('low') !== 'low'
              ? translate('low')
              : 'Baja',
            typeof translate === 'function' && translate('medium') !== 'medium'
              ? translate('medium')
              : 'Media',
            typeof translate === 'function' && translate('high') !== 'high'
              ? translate('high')
              : 'Alta',
          ],
          datasets: [
            {
              data: [
                alarmsByTasaAcumulado.baja,
                alarmsByTasaAcumulado.media,
                alarmsByTasaAcumulado.alta,
              ],
              backgroundColor: ['#1c6265', '#75481e', '#75283c'],
              borderColor: ['rgba(255,255,255,0)', 'rgba(255,255,255,0)', 'rgba(255,255,255,0)'],
              borderWidth: 1,
            },
          ],
        };
  }, [
    alarmsByTasaAcumulado.alta,
    alarmsByTasaAcumulado.baja,
    alarmsByTasaAcumulado.media,
    sumDonut,
    translate,
  ]);

  const latestRef = useRef({ chartData, jaulaFiltrada, selectedDay, sumDonut, selectedRateKey });
  latestRef.current = { chartData, jaulaFiltrada, selectedDay, sumDonut, selectedRateKey };

  useEffect(() => {
    if (!canvasRef.current) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: 'doughnut',
      data: doughnutData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              boxWidth: window.innerWidth * 0.01,
              font: { size: window.innerWidth * 0.008 },
            },
          },
          tooltip: {
            enabled: false,
            external: (context) => {
              if (typeof window !== 'undefined') {
                if (externalTooltipHandler.raf) cancelAnimationFrame(externalTooltipHandler.raf);
                externalTooltipHandler.raf = requestAnimationFrame(() => externalTooltipHandler(context));
              } else {
                externalTooltipHandler(context);
              }
            },
          },
        },
      },
    });

    if (chartInstanceRef) chartInstanceRef.current = chartRef.current;

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      setTooltipOpen(false);
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.data = doughnutData;
    chartRef.current.update('none');
    tooltipLockedRef.current = false;
    setTooltipOpen(false);
  }, [doughnutData]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleClick = (event) => {
      if (!chartRef.current) return;
      const points = chartRef.current.getElementsAtEventForMode(
        event,
        'nearest',
        { intersect: true },
        true
      );

      if (points.length) tooltipLockedRef.current = true;
    };

    canvas.addEventListener('click', handleClick);
    return () => canvas.removeEventListener('click', handleClick);
  }, []);

  const externalTooltipHandler = useCallback((context) => {
    const { chart, tooltip } = context;
    const {
      chartData: currentChartData,
      jaulaFiltrada: currentCage,
      selectedDay: currentDay,
      sumDonut: currentSum,
      selectedRateKey: currentRate,
    } = latestRef.current;

    if (currentSum === 0 || !tooltip.dataPoints?.length) {
      if (!tooltipLockedRef.current) setTooltipOpen(false);
      return;
    }

    if (tooltip.opacity === 0) {
      if (tooltipLockedRef.current) return;
      if (!hideScheduledRef.current) {
        hideScheduledRef.current = true;
        hideTimerRef.current = setTimeout(() => {
          hideScheduledRef.current = false;
          if (!tooltipLockedRef.current) setTooltipOpen(false);
          hideTimerRef.current = null;
        }, 280);
      }
      return;
    }

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
      hideScheduledRef.current = false;
    }

    const dataPoint = tooltip.dataPoints[0];
    const datasetIndex = dataPoint.datasetIndex;
    const dataIndex = dataPoint.dataIndex;
    const backgroundColor = chart.data.datasets[datasetIndex].backgroundColor;
    const colorSquare = Array.isArray(backgroundColor)
      ? backgroundColor[dataIndex]
      : backgroundColor;
    const value = dataPoint.formattedValue;
    const rateKeys = ['baja', 'media', 'alta'];
    const currentRateKey = rateKeys[dataIndex];

    const targetItems = currentCage ? [currentCage] : currentChartData;
    const rateEntriesMap = new Map();

    targetItems.forEach((item) => {
      const alarmsByTasa = rateAlarmEntries(item, currentRate);

      alarmsByTasa.forEach((entry) => {
        if (!matchesPeriodLabel(entry.day, currentDay)) return;

        const rates = Array.isArray(entry?.value?.[currentRateKey]?.tasas)
          ? entry.value[currentRateKey].tasas
          : [];

        rates
          .filter((rate) => typeof rate === 'number' && rate > 0)
          .forEach((rate) => {
            const unit = entry.unit;
            const key = `${entry.day}|${rate}|${unit}`;

            if (!rateEntriesMap.has(key)) {
              rateEntriesMap.set(key, {
                rawDay: entry.day,
                day: formatDisplayDay(entry.day),
                value: rate,
                unit,
                names: new Map(),
              });
            }

            rateEntriesMap.get(key).names.set(item.name, item.label ?? item.name);
          });
      });
    });

    const rateEntries = Array.from(rateEntriesMap.values())
      .sort((a, b) => {
        const aDate = parseDateSafe(a.rawDay);
        const bDate = parseDateSafe(b.rawDay);

        if (aDate && bDate && aDate.getTime() !== bDate.getTime()) {
          return aDate.getTime() - bDate.getTime();
        }

        return Number(a.value) - Number(b.value);
      })
      .map((entry) => ({
        rawDay: entry.rawDay,
        day: entry.day,
        value: entry.value,
        unit: entry.unit,
        names: Array.from(entry.names)
          .sort(([a], [b]) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
          .map(([, label]) => label)
          .join(', '),
      }));

    setTooltipData({
      placement: 'top',
      color: colorSquare,
      currentRateKey,
      value,
      rateEntries,
    });
    setTooltipOpen(true);
  }, []);

  const getDayAndMonthStr = (dateStr) => {
    const [day, month] = dateStr.split('-').map(Number);
    const monthAbbrs = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${day} de ${monthAbbrs[month - 1]}`;
  };

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideScheduledRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!canvasRef.current?.contains(event.target)) {
        tooltipLockedRef.current = false;
        setTooltipOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  return (
    <Grid
      container
      justifyContent="center"
      item
      xs={3}
      style={{ height: '18vh', width: '100%', marginLeft: '0.5vw', marginTop: '-3vh' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', width: '88%' }}>
        <h2 className="h2" style={{ marginBottom: '0vh' }}>{t('alarm_rate', 'Alarmas por tasa')}:</h2>
        <h2 className="h2">
          {jaulaSeleccionada && selectedDay
            ? `${chartData.find((j) => j.name === jaulaSeleccionada)?.label ?? jaulaSeleccionada} - ${selectedDay}`
            : jaulaSeleccionada
              ? `${chartData.find((j) => j.name === jaulaSeleccionada)?.label ?? jaulaSeleccionada}`
              : selectedDay
                ? `${selectedDay}`
                : `${getDayAndMonthStr(dias.startDate)} ${t('to', 'to')} ${getDayAndMonthStr(dias.endDate)}`}
        </h2>
      </div>

      <div style={{ height: '14vh', width: '12vw' }}>
        <canvas ref={canvasRef} />
      </div>

      <Popper
        open={tooltipOpen}
        anchorEl={canvasRef.current?.parentElement}
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 12] } },
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
          { name: 'flip', enabled: true },
        ]}
        sx={{ zIndex: 1300, pointerEvents: 'auto', overflowY: 'hidden' }}
      >
        <Box
          elevation={3}
          sx={{
            p: '.5vw',
            maxHeight: '51vh',
            overflowY: 'auto',
            backgroundColor: 'rgba(0,0,0,0.96)',
            color: '#c3c3c3',
            border: '0.01vw solid #e94e1a',
            borderRadius: '.5vw',
            minWidth: 200,
          }}
          onMouseEnter={() => {
            tooltipLockedRef.current = true;
            if (hideTimerRef.current) {
              clearTimeout(hideTimerRef.current);
              hideTimerRef.current = null;
              hideScheduledRef.current = false;
            }
          }}
          onMouseLeave={() => {
            tooltipLockedRef.current = false;
            setTooltipOpen(false);
          }}
          onWheel={(event) => event.stopPropagation()}
        >
          <TooltipBody
            color={tooltipData.color}
            currentRateKey={tooltipData.currentRateKey}
            value={tooltipData.value}
            rateEntries={tooltipData.rateEntries}
            translate={translate}
          />
        </Box>
      </Popper>
    </Grid>
  );
};

export default DoughnutChart;
